import { LocalDate } from '@js-joda/core';
import { CURRENTLY_ACTIVE_PRACTICES } from '@teamexos/fit-shared';
import {
  DistanceUnit,
  FocusArea,
  MemberExperience,
  PerformanceQuality,
  WorkingStyle,
} from '../../../../graphql/types';
import { AdminDemoUserInput } from './coachTraining';

/**
 * Sales demo personas (MA-6873 / MA-6875).
 *
 * 27 personas = 3 employer groups x 9 archetypes (Sr. Leader x3, High Growth x3,
 * Young Employee x1, Shift Worker x2). Sourced from the "sales demo personas"
 * spreadsheet. Built DRY: a shared base + common onboarding answers, spread with
 * per-archetype / per-employer overrides.
 *
 * NOTE: wearable sleep history (with heart-rate samples) is now seeded via the
 * `sleepData` field on `SALES_BASE`, which spells out the full spreadsheet profile
 * ("6.50 hr avg w/ 2 dips of 4.5 hrs, 4x across 6 weeks" plus irregular RHR)
 * explicitly rather than relying on test-data-service defaults.
 */

/** Profile shown on the demo coach's card: `bio` is the description, `passions` the interests. */
export const SALES_DEMO_COACH_DATA = {
  bio: "I'm a certified performance coach who helps busy professionals build sustainable habits around movement, nutrition, sleep, and stress. Together we'll set a gameplan that fits your schedule and helps you show up as your best self at home and at work.",
  passions: [
    'Strength training',
    'Running',
    'Healthy cooking',
    'Hiking',
    'Mindfulness',
  ],
};

export const SALES_DEMO_CHALLENGE_IDS = {
  distance: '7cc18c9d-c8b4-4cf6-ae2b-194d6c38727e',
  steps: '0e3fed55-93cb-419f-8ee0-a8667989a0da',
  duration: 'd2b1ba6e-d50e-4ee7-aa8b-64ae2b226fd0',
  strengthActivity: '26bad246-a137-4841-8056-b5e41cb57193',
  allRounder: 'af6035c4-9078-422a-b96f-d53c78e90894',
} as const;

const RUNNING_GENRE_ID = 'ad70a3f1-dcd7-425b-8be1-36a00e304240';
const STRENGTH_GENRE_ID = '29dc7c44-ef62-4586-8e9f-c199233a70ae';

/** Activities seeded per member to fill the necessary minimums for the challenges. */
const SALES_DEMO_CHALLENGE_COMPLETION: NonNullable<
  AdminDemoUserInput['challengeActivities']
> = [
  {
    sourceId: RUNNING_GENRE_ID,
    count: 5,
    distance: { value: 10, unit: DistanceUnit.Miles },
  },
  { sourceId: STRENGTH_GENRE_ID, count: 7, durationSeconds: 45 * 60 },
];

/**
 * On-demand content (videos/articles/audio), newest-first, completed after
 * creation so it tops the feed. test-data-service lays the items out back-to-back
 * by their `durationSeconds`, ending ~2h ago, so the timeline reads like a real
 * member. Durations match the published content lengths.
 */
const SALES_DEMO_COMPLETED_CONTENT: NonNullable<
  AdminDemoUserInput['completedContent']
> = [
  {
    contentId: 'd41d0964-b997-4216-a83b-f103265e4810',
    durationSeconds: 5 * 60,
  }, // GLP-1 article
  {
    contentId: 'ce22b61a-4ef2-47b3-abeb-b49622185b9a',
    durationSeconds: 29 * 60,
  }, // Cycling — Sprint 38
  {
    contentId: 'e58168ff-8db0-4e60-9a94-2d77777d650d',
    durationSeconds: 4 * 60,
  }, // Sleep
  {
    contentId: 'a3d7fc5b-4735-4b82-a8d6-4ab0cdfe798b',
    durationSeconds: 12 * 60,
  }, // Recharge Break
];

type SalesPersonaGroup =
  | 'srLeader'
  | 'highGrowth'
  | 'youngEmployee'
  | 'shiftWorker';

/** Coach-note template buckets (spreadsheet "Notes" doc groups). */
type NoteGroup = 'onsiteOrHybrid' | 'virtual' | 'ftRemote' | 'shiftWorker';

/** A single scripted coach-chat message (see test-data-service `DemoChatMessage`). */
type ChatMessage = NonNullable<AdminDemoUserInput['messages']>[number];

interface Archetype {
  key: string;
  group: SalesPersonaGroup;
  sex: 'M' | 'F';
  /**
   * Public path of the avatar image for this archetype, uploaded and assigned to
   * the persona in every employer group — the same image is shared across all 3
   * clients (see `AVATAR_IMAGES`).
   */
  avatarImagePath: string;
  age: number;
  jobTitle: string;
  /** Confirmed focus area ("Focus Area" column); also drives the onboarding-survey choice. */
  focusArea: FocusArea;
  /**
   * @description unused for now, we're just grabbing the first ID returned by the backend
   */
  gameplanPerformanceQuality: PerformanceQuality;
  /** Height in decimal feet (e.g. 5'8" -> 5.67), matching coachTraining.tsx. */
  heightFt: number;
  weightLbs: number;
}

/**
 * Coach-note / chat group for a persona, derived from the employer working style
 * and whether the persona is a shift worker (the spreadsheet "Notes" and "Chats"
 * docs are organized by these buckets, not by individual persona).
 */
const noteGroupFor = (
  group: SalesPersonaGroup,
  workingStyle: WorkingStyle,
): NoteGroup => {
  if (group === 'shiftWorker') {
    return 'shiftWorker';
  }
  if (workingStyle === WorkingStyle.Remote) {
    return 'ftRemote';
  }
  return 'onsiteOrHybrid';
};

/**
 * Public avatar image paths for the demo personas, keyed by sex. Files live in
 * `public/sales-demo-user-images/` and are served at the site root, so they ship
 * with the web app. Each archetype pulls one image in order (5 female / 4 male
 * archetypes), and the same image is reused for that archetype across all 3
 * employer groups — so the images are shared per archetype, not unique per
 * persona. They are uploaded through the standard signed-url avatar flow at
 * member-creation time (see `useSalesDemoActions`).
 */
const AVATAR_IMAGES = {
  F: [
    '/sales-demo-user-images/woman1.jpg',
    '/sales-demo-user-images/woman2.jpg',
    '/sales-demo-user-images/woman3.jpg',
    '/sales-demo-user-images/woman4.jpg',
    '/sales-demo-user-images/woman5.jpg',
  ],
  M: [
    '/sales-demo-user-images/man1.jpg',
    '/sales-demo-user-images/man2.jpg',
    '/sales-demo-user-images/man3.jpg',
    '/sales-demo-user-images/man4.jpg',
  ],
} as const;

/**
 * The 9 archetypes, shared across all 3 employer groups. Attributes come from the
 * Fortify Talent (Onsite FT) block of the spreadsheet; only the name and employer
 * differ across the other two groups (see `EMPLOYERS`).
 */
const ARCHETYPES: Archetype[] = [
  {
    key: 'sr-leader-coo',
    group: 'srLeader',
    sex: 'F',
    avatarImagePath: AVATAR_IMAGES.F[0],
    age: 50,
    jobTitle: 'Chief Operations Officer',
    focusArea: FocusArea.LongevityHealthyAging,
    gameplanPerformanceQuality: PerformanceQuality.SelfRegulation, // gameplanName: 'Self Management',
    heightFt: 5.67,
    weightLbs: 180,
  },
  {
    key: 'sr-leader-vp-eng',
    group: 'srLeader',
    sex: 'F',
    avatarImagePath: AVATAR_IMAGES.F[1],
    age: 43,
    jobTitle: 'VP of Engineering',
    focusArea: FocusArea.FemaleWellBeing,
    gameplanPerformanceQuality: PerformanceQuality.Flowability, // gameplanName: 'Flowability',
    heightFt: 5.67,
    weightLbs: 180,
  },
  {
    key: 'sr-leader-managing-director',
    group: 'srLeader',
    sex: 'M',
    avatarImagePath: AVATAR_IMAGES.M[0],
    age: 35,
    jobTitle: 'Managing Director',
    // Spreadsheet "Daily Performance" has no dedicated FocusArea; mapped to Fitness
    // as the closest match pending product confirmation.
    focusArea: FocusArea.Fitness,
    gameplanPerformanceQuality: PerformanceQuality.LoadAccumulation, // gameplanName: 'Load accumulation',
    heightFt: 6.0,
    weightLbs: 230,
  },
  {
    key: 'high-growth-surgical-resident',
    group: 'highGrowth',
    sex: 'M',
    avatarImagePath: AVATAR_IMAGES.M[1],
    age: 32,
    jobTitle: 'Sr. Surgical Resident',
    focusArea: FocusArea.Sharpness,
    gameplanPerformanceQuality: PerformanceQuality.NutrientProfile, // gameplanName: 'Digestive & Metabolic Function',
    heightFt: 6.0,
    weightLbs: 215,
  },
  {
    key: 'high-growth-product-manager',
    group: 'highGrowth',
    sex: 'M',
    avatarImagePath: AVATAR_IMAGES.M[2],
    age: 33,
    jobTitle: 'Product or Portfolio Manager',
    focusArea: FocusArea.BasicHealth,
    gameplanPerformanceQuality: PerformanceQuality.BodyComposition, // gameplanName: 'Body Composition',
    heightFt: 5.67,
    weightLbs: 215,
  },
  {
    key: 'high-growth-sales',
    group: 'highGrowth',
    sex: 'F',
    avatarImagePath: AVATAR_IMAGES.F[2],
    age: 31,
    jobTitle: 'Sales',
    focusArea: FocusArea.MuscleGrowth,
    gameplanPerformanceQuality: PerformanceQuality.FvProfile, // gameplanName: 'Force Production',
    heightFt: 5.67,
    weightLbs: 165,
  },
  {
    key: 'young-employee-jr-developer',
    group: 'youngEmployee',
    sex: 'F',
    avatarImagePath: AVATAR_IMAGES.F[3],
    age: 29,
    jobTitle: 'Jr. Software Developer or Analyst',
    focusArea: FocusArea.MentalEmotionalWellBeing,
    gameplanPerformanceQuality: PerformanceQuality.InnerAwareness, // gameplanName: 'Awareness',
    heightFt: 5.67,
    weightLbs: 170,
  },
  {
    key: 'shift-worker-nurse',
    group: 'shiftWorker',
    sex: 'F',
    avatarImagePath: AVATAR_IMAGES.F[4],
    age: 32,
    jobTitle: 'Nurse',
    focusArea: FocusArea.WeightLoss,
    gameplanPerformanceQuality: PerformanceQuality.BodyComposition, // gameplanName: 'Body Composition',
    heightFt: 5.67,
    weightLbs: 180,
  },
  {
    key: 'shift-worker-manufacturing',
    group: 'shiftWorker',
    sex: 'M',
    avatarImagePath: AVATAR_IMAGES.M[3],
    age: 34,
    jobTitle: 'Manufacturing or Global Security',
    focusArea: FocusArea.Sharpness,
    gameplanPerformanceQuality: PerformanceQuality.NutrientProfile, // gameplanName: 'Nutrient Profile',
    heightFt: 6.0,
    weightLbs: 230,
  },
];

interface Employer {
  key: string;
  id: string;
  employer: string;
  workingStyle: WorkingStyle;
  /**
   * This client's in-person studio and its location, where members buy their
   * gym membership. Every demo client shares one MBO site, so a member bought
   * in at another client's studio gets a second membership at their own when
   * the MBO sync runs. The demo coach is also assigned to `locationId`, since
   * the app's office picker only lists locations with a coach of the client.
   */
  studioId: string;
  locationId: string;
  /**
   * Training plan assigned to this client's members ("Training Plan" column).
   * Defined per client rather than derived from working style.
   */
  trainingPlan: NonNullable<AdminDemoUserInput['trainingPlan']> | undefined;
  /** Persona names in archetype order (index-aligned with `ARCHETYPES`). */
  names: string[];
}

export const SALES_DEMO_EMPLOYERS: Employer[] = [
  {
    id: 'c2c2362b-a948-41eb-96de-0515179edec0',
    key: 'fortify-talent',
    employer: 'Fortify Talent',
    workingStyle: WorkingStyle.Office,
    studioId: '3212b7c1-21db-4022-bc3d-7b64b7daa0d6', // Fortify Talent Gym
    locationId: '26a622ec-e478-4eb0-b35d-b72a2bb5f762', // Fortify Talent
    trainingPlan: undefined,
    names: [
      'Sonia Sprint',
      'Sarah Steady',
      'Samuel Statman',
      'Harlow Heals',
      'Hiro Hackers',
      'Hannah Hedge',
      'Yara Youthful',
      'Wendy Well',
      'Wade Watch',
    ],
  },
  {
    id: 'e4ca62cd-d460-452b-a3a9-575dbfb9ef73',
    key: 'happy-human-corp',
    employer: 'Happy Human Corp.',
    workingStyle: WorkingStyle.Hybrid,
    studioId: 'ca47f713-3d72-4cf2-85b6-aa7c862413e0', // Happy Human Corp. Gym
    locationId: 'd2613cbb-5bbf-4e40-8ae6-2c6cac3711ce', // Office Building 1
    trainingPlan: undefined,
    names: [
      'Skylar Habit',
      'Sienna Health',
      'Hudson Hustle',
      'Hunter Hybrid',
      'Hayes Healthy',
      'Halle Holistic',
      'Yasmine Habbit',
      'Sara Hyper',
      'Steve Holder',
    ],
  },
  {
    id: 'b07252db-98bd-442c-831c-9e59949e320f',
    key: 'resiliance-rising',
    employer: 'Resiliance Rising',
    workingStyle: WorkingStyle.Remote,
    studioId: '3cc860dc-1c26-416b-bab6-3cd89bc3ba25', // Resilience Rising Studio
    locationId: '25259654-e468-4b69-a374-5b105e91ec63', // Resilience Rising Location
    trainingPlan: undefined,
    names: [
      'Remt Reset',
      'Ria Runner',
      'Ryder Ready',
      'Rumi Reach',
      'Rory Rise',
      'Ruby Radiant',
      'Ramona Relax',
      'Roxie Result',
      'Ronin Renew',
    ],
  },
];

/**
 * `FocusArea` -> onboarding-survey choice index (the option order shown to the
 * member, which differs from the `FocusArea` enum order). Mirrors test-data-service's
 * sales-persona lookups.
 *
 * Choice order: 0 Fitness, 1 Weight Loss, 2 Mental & Emotional Well-Being,
 * 3 Longevity & Healthy Aging, 4 Muscle Growth, 5 Sharpness,
 * 6 Female Well-Being, 7 Basic Health.
 */
const FOCUS_SURVEY_INDEX: Record<FocusArea, number> = {
  [FocusArea.Fitness]: 0,
  [FocusArea.WeightLoss]: 1,
  [FocusArea.MentalEmotionalWellBeing]: 2,
  [FocusArea.LongevityHealthyAging]: 3,
  [FocusArea.MuscleGrowth]: 4,
  [FocusArea.Sharpness]: 5,
  [FocusArea.FemaleWellBeing]: 6,
  [FocusArea.BasicHealth]: 7,
};

/** Plan items marked complete on the assigned gameplan ("in-progress" look). */
const GAMEPLAN_COMPLETED_PLAN_ITEMS_COUNT = 5;

/**
 * Coach-note copy per persona group, from `notes-for-sales-demo-data.md`.
 *
 * The notes doc defines four buckets (Onsite FT & Hybrid, Virtual, FT Remote,
 * Shift Worker). `noteGroupFor` currently assigns three of them (Office/Hybrid ->
 * onsiteOrHybrid, Remote -> ftRemote, shift -> shiftWorker); `virtual` is kept
 * here for completeness but is not assigned to any persona today.
 */
const NOTE_CONTENT: Record<NoteGroup, string> = {
  onsiteOrHybrid: `<p><strong>Family:</strong> 2 kids in middle school.</p><p><strong>Goals:</strong> Get body &amp; energy back. Confidence back for family and leadership. Body comp (need to capture initial). Energy to get through the day and at the end of the day with family.</p><ul><li><p>Sleep is the first stone to unturn</p></li><li><p>Nutrition &amp; movement in the future</p></li></ul><p><strong>Routine:</strong> Nothing consistent for the past 3 years. Heads down in work and family. Used to play basketball in HS. Peloton purchased over Covid that gets intermittent use. Commute 40-75 min, so training must be done mid-day or before leaving in the PM.</p><p><strong>Gameplan:</strong></p><ul><li><p>Sleep routine: non-existent - practice sent to ID 2 non-negotiables</p></li><li><p>Strength train 2x/wk (15-30 min) onsite FCM group training &amp; TP</p></li><li><p>Recharge breaks, move more, tie in family - 2x/wk to get daily steps up. Current: 2x/wk AM walks, 2x/wk family walks, 2x/wk afternoon recharge breaks</p></li></ul><p><strong>Next steps:</strong></p><ol><li><p>Likes checking the boxes weekly but no time for chat. Follow up every 2 weeks via chat</p></li><li><p>Sleep 2 non-negotiables post practice in chat or follow-up meeting</p></li><li><p>Formal check-in every 6 weeks for progress and Gameplan assessment</p></li><li><p>Confidence will be an area to build and measure</p></li><li><p>Body comp &amp; progress with Gameplan assessments</p></li></ol>`,
  virtual: `<p><strong>Family:</strong> 2 kids in middle school.</p><p><strong>Goals:</strong> Get body &amp; energy back. Confidence back for family and leadership. Body comp (need to capture initial). Energy to get through the day and at the end of the day with family.</p><ul><li><p>Sleep is the first stone to unturn</p></li><li><p>Nutrition &amp; movement in the future</p></li></ul><p><strong>Routine:</strong> Nothing consistent for the past 3 years. Heads down in work and family. Used to play basketball in HS. Peloton purchased over Covid that gets intermittent use. No weight training currently or weights at home - wants to start BW then purchase weights. Gym membership but doesn't have time most days.</p><p><strong>Gameplan:</strong></p><ul><li><p>Sleep routine: non-existent - practice sent to ID 2 non-negotiables</p></li><li><p>Strength train 2x/wk (15-30 min) BW only currently</p></li><li><p>Recharge breaks, move more, tie in family - 2x/wk to get daily steps up. Current: 2x/wk AM walks, 2x/wk family walks, 2x/wk afternoon recharge breaks</p></li></ul><p><strong>Next steps:</strong></p><ol><li><p>Follow up every 2 weeks via chat</p></li><li><p>Sleep 2 non-negotiables post practice</p></li><li><p>Formal check-in every 6 weeks for progress and Gameplan assessment</p></li><li><p>Confidence will be an area to build and measure</p></li><li><p>Body comp &amp; progress with Gameplan assessments</p></li></ol>`,
  ftRemote: `<p><strong>Family:</strong> 2 kids in middle school.</p><p><strong>Goals:</strong> Get body &amp; energy back. Confidence back for family and leadership. Body comp. Energy to get through the day and at the end of the day with family.</p><p><strong>Pain:</strong> Knee pain for the past 3 years going down stairs or lunging / going down to the ground.</p><p><strong>Routine:</strong> Nothing consistent for the past 3 years. Heads down in work and family. Used to play basketball in HS. Peloton purchased over Covid that gets intermittent use, but not with current knee pain.</p><p><strong>Gameplan:</strong></p><ul><li><p>Strength train 2x/wk (15-30 min) BW program - if consistent, needs weights</p></li><li><p>Walk more, 7-10k steps/day</p></li><li><p>Recharge breaks to get daily steps up: morning 2x/wk, work breaks, family after dinner</p></li><li><p>Knee pain practice &amp; mobility + stability for knees</p></li></ul><p><strong>Next steps:</strong> Follow up every 2 weeks via chat. Formal check-in every 6 weeks for progress and Gameplan. Nutrition &amp; sleep check-in to see if they are ready as this priority. Confidence will be an area to build and measure. Body comp &amp; progress with process / Gameplan. Accountability in the gameplan with activity feed, challenges, work group - TBD next meeting.</p>`,
  shiftWorker: `<p><strong>Family:</strong> 2 kids in middle school.</p><p><strong>Goals:</strong> Get body &amp; energy back. Confidence back. Body comp. Energy to get through the day and at the end of the day with family.</p><p><strong>Pain:</strong> Knee pain for the past 3 years going down stairs or lunging / going down to the ground. Daily grind to make it through. Starting to take a real mental toll.</p><p><strong>Routine:</strong> Nothing consistent for the past 3 years. Heads down in work and family. Used to play basketball in HS. Has a Peloton at home but too exhausted at the end of the day from being on feet all day.</p><p><strong>Gameplan:</strong> Get rid of knee pain.</p><ul><li><p>Knee pain practice (10 min) 2x/wk evening, mostly laying down</p></li><li><p>Knee mobility &amp; stability (15 min) 2x/wk before work</p></li><li><p>Strength train 2x/wk (15 min) - program "Start Your Engine"</p></li></ul><p><strong>Next steps:</strong> Follow up every 2 weeks via chat. Nutrition &amp; sleep check-in to see if they are ready as this priority - training might take care of sleep and/or nutrition but will be key with the energy goal. Confidence will be an area to build and measure. Accountability in the gameplan with activity feed, challenges, work group - TBD next meeting.</p>`,
};

/**
 * Coach-chat scripts from `chats-female-demo-data.md` / `chats-male-demo-data.md`.
 *
 * Like the coach notes, the scripts are organized by the `noteGroupFor` bucket
 * (Onsite FT & Hybrid, Fully Remote, Shift Worker) and by sex. Each script is the
 * opening coach gameplan, a few resource links sent as separate chats, a member
 * reply, and — for non-remote personas — a coach community-event follow-up and a
 * final member reply. Female personas also receive the cycle-health article.
 *
 * Markdown escapes from the source docs are unescaped; curly quotes are normalized
 * to straight quotes.
 */
const FEMALE_ONSITE_OPENING = `Great connecting today! I've outlined our gameplan to align with your GOAL, REGAIN YOUR ENERGY & CONFIDENCE in your body to show up as a better version at home & work.

**Gameplan Summary:**

* **Sleep:** Start the "Power Down for Bed" practice. We'll identify 2 non-negotiables at our next follow-up to keep your routine consistent at home and on the road.
* **Training:** 2x weekly strength sessions at fitness center. (Travel workouts are linked below for your busy weeks).
* **Movement:** Targeting **7k–10k steps/day**. We'll hit this through a mix of morning walks 2x/wk (mon & Fri), family walks (2x/wk evening), and mid-day recharge breaks (linked below).

**The Big Picture:** We're tracking your progress at the 6-week and 6-month marks, focusing on lower stress and higher energy for your family.

I'm in your corner! Reach out here anytime if you need a modification or have a quick question.`;

const FEMALE_REMOTE_OPENING = `Great connecting today! I've outlined our gameplan to align with your GOAL, REGAIN YOUR ENERGY & CONFIDENCE in your body to show up as a better version at home & work.

**Gameplan Summary:**

* **Sleep:** Start the "Power Down for Bed" practice. We'll identify 2 non-negotiables at our next follow-up to keep your routine consistent at home and on the road.
* **Training:** 2x weekly strength Training Plan & Ondemand options. (Travel workouts are linked below for your busy weeks).
* **Movement:** Targeting **7k–10k steps/day**. We'll hit this through a mix of morning walks 2x/wk (mon & Fri), family walks (2x/wk evening), and mid-day recharge breaks (linked below).

**The Big Picture:** We're tracking your progress at the 6-week and 6-month marks, focusing on lower stress and higher energy for your family.

I'm in your corner! Reach out here anytime if you need a modification or have a quick question.`;

const MALE_ONSITE_OPENING = `Great connecting today! I've outlined our gameplan to align with your GOAL, REGAIN YOUR ENERGY & CONFIDENCE in your body to show up as a better version at home & work.

**Gameplan Summary:**

* **Sleep:** Start the "Power Down for Bed" practice.
  * We'll identify 2 non-negotiables at our next follow-up to keep your routine consistent at home and on the road.
* **Training:** 2x weekly strength sessions at fitness center.
  * Training Plan for days you can't make the group classes.
  * Travel workouts are linked below for your busy weeks.
* **Movement:** Targeting **7k–10k steps/day**. We'll hit this through a mix of morning walks 2x/wk (mon & Fri), family walks (2x/wk evening), and mid-day recharge breaks (linked below).

**The Big Picture:** We're tracking your progress at the 6-week and 6-month marks, focusing on lowering stress and higher energy for you, your, family & work.

I'm in your corner! Reach out here anytime if you need a modification or have a quick question.`;

const MALE_REMOTE_OPENING = `Great connecting today! I've outlined our gameplan to align with your GOAL, REGAIN YOUR ENERGY & CONFIDENCE in your body to show up as a better version at home & work.

**Gameplan Summary:**

* **Sleep:** Start the "Power Down for Bed" practice.
  * We'll identify 2 non-negotiables at our next follow-up to keep your routine consistent at home and on the road.
* **Training:** 2x weekly strength
  * Training Plan - when you go to gym
  * Ondemand for busy days your dont have time to get to the gym
* **Daily Movement:** Targeting **7k–10k steps/day**. We'll hit this through a mix of morning walks 2x/wk (mon & Fri), family walks (2x/wk evening), and mid-day recharge breaks (linked below).

**The Big Picture:** We're tracking your progress at the 6-week and 6-month marks, focusing on lower stress and higher energy for your family.

I'm in your corner! Reach out here anytime if you need a modification or have a quick question.`;

// Shift-worker opening is identical in the female and male source docs.
const SHIFT_WORKER_OPENING = `Great connecting today! I've outlined our gameplan to align with your GOAL, REGAIN YOUR ENERGY & CONFIDENCE in your body to show up as a better version at home & work.

**Your Focus Areas:**

* **Sleep:** Start the "Power Down for Bed" practice.
  * We'll identify 2 non-negotiables at our next follow-up.
* **Training:** Get rid of knee pain
  * 2x/wk strength sessions body weight can be performed anywhere.
    * Modifications are in your Training Plan
  * 2X/wk Knee pain focused mobility & stability classes
* **Daily** **Movement:**
  * Recharge Breaks can be performed at the beginning, break period, or end of day.
  * Stack these 5-10 minutes and by the end of the week, month we have big gains!

* **The Big Picture:** Get rid of the knee pain & build a better sleep routine. Then we can focus on building back up your battery to give you the energy to make it through a day while having energy for your family when your home. 6 Week push to focus on getting rid of knee pain.

I'm in your corner! Reach out here anytime if you need a modification or have a quick question.`;

// Shared resource links (each sent as a separate chat) and member/coach replies.
const TRAVEL_WORKOUTS_MSG =
  'Travel workouts: https://fit.teamexos.com/ondemand/6eaab5b2-467e-4b57-80b2-46953bf52950';
const WALKING_AUDIO_MSG =
  'Walking Audio Tracks: https://fit.teamexos.com/ondemand/6eaab5b2-467e-4b57-80b2-46953bf52950';
const CYCLE_ARTICLE_MSG =
  '[How your Cycle impacts Energy, Recovery & More](https://fit.teamexos.com/ondemand/f2063fc0-03a8-4d20-80e8-662bd0a6a365)';
const SHIFT_WORKER_TIPS_MSG =
  '5 Tips for Shift Workers\nhttps://fit.teamexos.com/ondemand/230d76eb-9609-494f-81a0-74c1a49b6e41';
const RECHARGE_BREAK_MSG =
  '[Recharge Break Improve Mental Focus](https://fit.teamexos.com/ondemand/a3d7fc5b-4735-4b82-a8d6-4ab0cdfe798b)';
const MEMBER_THANKS_NEEDED_MSG =
  'Thanks for the sleep routine practice resources. Excited to work through this upgrade as its needed!';
const MEMBER_THANKS_MOJO_MSG =
  'Thanks for the sleep routine practice resources. Excited to work through this upgrade and get my mojo back!';
const COACH_COMMUNITY_EVENT_MSG =
  'There is a great community event next week you should get your team together outside of meetings and join us in the cafe Tue & Thurs for 30 min at 11:30, 1230 & 1:30 Meal Prep & Eat! Include this PDF flyer https://docs.google.com/document/d/1IzD-AlFlQYyXgHe20XgKrKCY52jxcuVVMNLv56_zdTc/edit?tab=t.0';
const MEMBER_THANKS_EVENT_MSG =
  'Thanks for the great idea to get the team together outside of work!';

const coachMsg = (text: string): ChatMessage => ({ from: 'coach', text });
const memberMsg = (text: string): ChatMessage => ({ from: 'member', text });

/**
 * Builds the scripted coach-chat exchange for a persona from its sex and note/chat
 * bucket. The unused `virtual` bucket falls through to the onsite/hybrid script.
 */
const getChatScript = (sex: 'M' | 'F', group: NoteGroup): ChatMessage[] => {
  if (group === 'shiftWorker') {
    return [
      coachMsg(SHIFT_WORKER_OPENING),
      coachMsg(SHIFT_WORKER_TIPS_MSG),
      coachMsg(RECHARGE_BREAK_MSG),
      memberMsg(MEMBER_THANKS_NEEDED_MSG),
      coachMsg(COACH_COMMUNITY_EVENT_MSG),
      memberMsg(MEMBER_THANKS_EVENT_MSG),
    ];
  }

  // Female personas also receive the cycle-health article as a separate chat.
  const linkMsgs =
    sex === 'F'
      ? [
          coachMsg(TRAVEL_WORKOUTS_MSG),
          coachMsg(CYCLE_ARTICLE_MSG),
          coachMsg(WALKING_AUDIO_MSG),
        ]
      : [coachMsg(TRAVEL_WORKOUTS_MSG), coachMsg(WALKING_AUDIO_MSG)];

  if (group === 'ftRemote') {
    return [
      coachMsg(sex === 'F' ? FEMALE_REMOTE_OPENING : MALE_REMOTE_OPENING),
      ...linkMsgs,
      memberMsg(MEMBER_THANKS_MOJO_MSG),
    ];
  }

  return [
    coachMsg(sex === 'F' ? FEMALE_ONSITE_OPENING : MALE_ONSITE_OPENING),
    ...linkMsgs,
    memberMsg(MEMBER_THANKS_NEEDED_MSG),
    coachMsg(COACH_COMMUNITY_EVENT_MSG),
    memberMsg(MEMBER_THANKS_EVENT_MSG),
  ];
};

/**
 * Onboarding answers shared by all 27 personas.
 *
 * The capacity answers are tuned to the spreadsheet capacity targets — Training 50,
 * Movement 50, Sleep 60, Nutrition/Fueling 70, Reflection 60, Regulation 40 — as closely
 * as the discrete well-being answer scales allow. Each section score averages only its
 * answered questions, and only well-being questions are answered (the HPC chapter
 * questions are never answered for these non-HPC demo members), so several sections are
 * driven by a single answer on a quarter-step scale and cannot hit the target exactly:
 *   - Training 50 (exact), Sleep 58.3, Fueling 75, Reflection 62.5 — closest reachable.
 *   - Movement 60 and Regulation 50 are ±10: legReach has no 50, frequency has no 40, and
 *     each is driven by a single well-being answer.
 * `motivation: 1` = "ready, but need help getting started"; `experience: 1` = "I go
 * through phases".
 *
 * `passions` / `barriers` are multi-select choice indices encoding the spreadsheet
 * values (Cycling/running/travel/science/hiking; Time/Family/Pain or Injury/No Energy/
 * Work). Note the option labels differ slightly: "Cycling" = "Biking", "Pain or Injury"
 * = "Pain or injury".
 */
const COMMON_SURVEY = {
  motivation: 1,
  experience: 1,
  movement1: 2,
  movement2: 3,
  nutrition1: 3,
  nutrition2: 3,
  recovery1: 3,
  recovery2: 2,
  recovery3: 2,
  mindset1: 3,
  mindset2: 2,
  mindset3: 2,
  // Cycling(Biking), running, travel, science, hiking
  passions: [1, 12, 18, 14, 5] as number[],
  // Time, Family, Pain or Injury, No Energy, Work
  barriers: [0, 2, 3, 4, 6] as number[],
} as const;

const SALES_MBO_MEMBERSHIP_CONTRACT_ID = 125; // "In-Person" membership contract id at MBO studio -2147483535

/** Fields common to all 27 personas. */
const SALES_BASE = {
  memberExperience: MemberExperience.CoachLed,
  shouldCreateGymArrival: true,
  shouldCreateHistoricalData: true,
  shouldAssignPractice: CURRENTLY_ACTIVE_PRACTICES.find(
    (practice) => practice.name === 'Calm your mind before bed',
  )?.id,
  completeGameplanPreassessments: true,
  // Wearable sleep history with heart-rate samples, encoding the spreadsheet
  // "Wearable" / "Sleep" columns: "6.50 hr avg w/ 2 dips of 4.5 hrs, 4x across 6
  // weeks" plus an irregular resting heart rate. Every option is specified
  // explicitly rather than relying on test-data-service defaults, so this file
  // remains the source of truth for the demo profile.
  sleepData: {
    count: 42, // 6 weeks of nights
    withDeepRem: true,
    averageSleepHours: 6.5,
    dip: {
      hours: 4.5,
      nightCount: 2, // "2 dips of 4.5 hrs"
      occurrences: 4, // "4x across 6 weeks"
    },
    irregularHeartRate: true, // "RHR showing irregular"
  },
  // walk activities are seeded a few days per week across the span, and those days
  // land at 7,000–10,000 steps with a 3,000–5,000 background count on the
  // other days.
  stepsData: {
    count: 42, // 6 weeks of days
    walksPerWeek: 3,
    shouldCreateWalkActivities: true,
    walkStepRange: { min: 7000, max: 10000 },
    baselineStepRange: { min: 3000, max: 5000 },
  },
  challengeIds: Object.values(SALES_DEMO_CHALLENGE_IDS),
  challengeActivities: SALES_DEMO_CHALLENGE_COMPLETION,
  completedContent: SALES_DEMO_COMPLETED_CONTENT,
  // The coach-chat script is set per persona (see `messages` below), since it varies
  // by sex and chat bucket — it replaces the single canned `shouldSendMessage`.
} satisfies Partial<AdminDemoUserInput>;

export const getCoachSlug = (coachEmail: string) => coachEmail.split('@')[0];

/**
 * Returns the 27 sales demo persona definitions as `AdminDemoUserInput`, ready for
 * the Test Data tool's `createPredefinedMembers` -> `createDemoUser` path.
 */
export const getSalesDemoMembers = ({
  coachId,
  coachEmail,
  nutritionistId,
  fitClientId,
  gameplanTemplateId,
}: {
  coachId: string;
  coachEmail: string;
  nutritionistId?: string;
  fitClientId: string;
  gameplanTemplateId?: string | null;
}): AdminDemoUserInput[] =>
  SALES_DEMO_EMPLOYERS.filter(
    (employer) => employer.id === fitClientId,
  ).flatMap((employer) =>
    ARCHETYPES.map((archetype, index): AdminDemoUserInput => {
      const [firstName, ...rest] = employer.names[index].split(' ');
      const lastName = rest.join(' ');
      const noteGroup = noteGroupFor(archetype.group, employer.workingStyle);

      return {
        ...SALES_BASE,
        officeLocationId: employer.locationId,
        contractsToPurchase: [
          {
            studioId: employer.studioId,
            locationId: employer.locationId,
            contractId: SALES_MBO_MEMBERSHIP_CONTRACT_ID,
          },
        ],
        coachId,
        nutritionistId,
        localId: `${employer.key}-${archetype.key}`,
        avatarImagePath: archetype.avatarImagePath,
        firstName,
        lastName,
        email: `${getCoachSlug(coachEmail)}.sales-${employer.id.slice(0, 3)}-${index.toString().padStart(2, '0')}@testuser.ex-os.net`,
        pronoun: archetype.sex === 'F' ? 'She / Her' : 'He / Him',
        workingStyle: employer.workingStyle,
        memberFocusArea: archetype.focusArea,
        onboardingSurveys: {
          ...COMMON_SURVEY,
          birthday: LocalDate.now().minusYears(archetype.age).toString(),
          height: archetype.heightFt,
          weight: archetype.weightLbs,
          identity: archetype.sex === 'F' ? 1 : 0,
          focusArea: FOCUS_SURVEY_INDEX[archetype.focusArea],
        },
        note: {
          coachId,
          title: 'Coaching Session Note',
          content: NOTE_CONTENT[noteGroup],
        },
        memberExperience: gameplanTemplateId
          ? MemberExperience.CoachLed
          : MemberExperience.SelfLed,
        messages: getChatScript(archetype.sex, noteGroup),
        ...(gameplanTemplateId
          ? {
              assignCustomPlanTemplate: {
                id: gameplanTemplateId,
                completedPlanItemsCount: GAMEPLAN_COMPLETED_PLAN_ITEMS_COUNT,
              },
            }
          : {}),
        trainingPlan: undefined,
        fitClientId: employer.id,
      };
    }),
  );
